<template>
  <div></div>
</template>

<script lang="ts">
import { Component, Prop, PropSync, Vue, Watch } from 'vue-property-decorator';
import { IChartAttributes, ILine, IDataPoint } from './ChartInterfaces';
import { sprintf } from 'sprintf-js';
import { DateTime } from "luxon";
import 'chartjs-adapter-luxon';
import base64url from 'base64url';

import { Bar } from 'vue-chartjs/legacy';
import { Line as LineChartGenerator } from 'vue-chartjs/legacy';

import { Chart as ChartJS, LineController, LineElement, TimeScale, PointElement, Title, Tooltip, Legend, BarElement, CategoryScale, LinearScale } from 'chart.js';


const HorizontalLinePlugin = {
  id: 'horizontal',
  
  afterDraw: (chart: any, args: object, options: any) => {
    
    let lines: any = options.lines;
    let backgroundColor = options.canvasColour;

    const ctx = chart.canvas.getContext('2d');
    ctx.save();
    ctx.globalCompositeOperation = 'destination-over';
    ctx.fillStyle = backgroundColor;
    ctx.fillRect(0, 0, chart.width, chart.height);
    ctx.restore();

    if (lines === undefined || lines === null) {
      return;
    }

    if (!Array.isArray(lines) || lines.length === 0) {
      return;
    }

    ctx.save();

    for (let line of lines) {

      let scaleId = line.scaleId;
      var yScale = chart.scales[scaleId];
      let value = line.value;
      let pixel_value = yScale.getPixelForValue(value);
      let lineColor = line.lineColor;
      let lineWidth = line.lineWidth;
      let lineDash = line.lineDash;

      ctx.lineWidth = lineWidth;

      ctx.beginPath();
      ctx.moveTo(0, pixel_value);
      ctx.lineTo(chart.canvas.width, pixel_value);
      ctx.setLineDash(lineDash);
      ctx.strokeStyle = lineColor;
      ctx.stroke();
    }

    ctx.restore();
  },
};

// annotationPlugin
ChartJS.register(Title, HorizontalLinePlugin, LineController, LineElement, PointElement, Tooltip, Legend, BarElement, CategoryScale, TimeScale, LinearScale);

export enum AveragingType {
    None = 'None',
    AverageDayInMonth = 'AverageDayInMonth',
    AverageOfMonth = 'AverageOfMonth',
}

export function EnumProp<T extends string | number>(d: T, e: Record<T, any>) {
    return {
        default: d,
        validator(v: T) {
            return e[v] !== undefined;
        },
    };
}

@Component({
  components: { Bar, LineChartGenerator },
})
export default class BaseChart extends Vue {

  @Prop({default: () => ([]), type: Array}) public lines!: ILine[];
  @Prop({default: '200px', type: String}) public height!: string;
  @Prop({default: () => (''), type: String}) public title!: string;
  @Prop() public ylabel!: string;
  @Prop() public mainPlotTitle!: string;
  @Prop() public aggregate!: string;
  @Prop() public granularity!: string;
  @Prop({type: String}) public xMin!: string;

  @Prop({default: undefined, type: String}) public xMax!: string;
  @Prop({default: 'ha', type: String}) public displayFormat!: string;
  @Prop({default: undefined, type: String}) public timeUnit!: string;
  @Prop({default: 0, type: Number}) public yMin!: number;
  @Prop({default: 0, type: Number}) public yMax!: number;
  @Prop({default: true, type: Boolean}) public lineChartType!: boolean;
  @Prop({default: false, type: Boolean}) public rollingAverage!: boolean;
  @Prop({default: false, type: Boolean}) public debug!: boolean;
  @Prop({default: '#102841', type: String}) public canvasColour!: string;
  @Prop({default: '#FFFFFF', type: String}) public textColour!: string;

  @Prop({default: () => (''), type: String}) public url!: string;
  @Prop({default: () => ([]), type: Array}) public chartAttributes!: IChartAttributes[];

  protected xMinShadowed: string = '';
  protected extra_lines: ILine[] = [];
  protected showLoading: boolean = true;
  protected data: any[][] = [];
  // protected daily_data: any[] = [];
  protected data_rolling_average: any[] = [];

  protected hasReadData: boolean = false;

  protected loaded: boolean = false;

  public created() {
    this.xMinShadowed = this.xMin;
  }

  public async mounted() {
    console.log('mounted BarChart');

    // this.localxMin = DateTime.now().toUTC().minus({months:13}).toFormat("yyyy-MM-dd'T'HH:mm:ss");
    // alert(this.xMin)
    if (this.chartAttributes.length > 0) {
      await this.readData();
    }
  }

  @Watch('chartAttributes')
  public async onPropertyRefsChanged(value: string, oldValue: string) {
    // Do stuff with the watcher here.
    if (this.chartAttributes.length > 0) {
      await this.readData();
    }
  }

  protected async readData(): Promise<any> {
    console.error('readData needs implementing');
  }

  private get horizontal_lines(): object[] {

    if (this === undefined || this === null) {
      return [];
    }

    const self = this;

    const tmp: object[] = [];

    if (self.lines === undefined || self.lines === null) {
      return [];
    }

    if (!Array.isArray(self.lines) || self.lines.length === 0) {
      return [];
    }

    const all_lines = self.lines.concat(self.extra_lines); 

    for (const line of all_lines) {

      tmp.push(
        {
          scaleId: 'y',
          value: line.value,
          lineColor: line.colour,
          lineWidth: 2,
          lineDash: [3, 4],
        }
      );
    }
  
    return tmp;
  }
  
  public get myChartOptions(): object | undefined {

    if (this === undefined || this === null) {
      return undefined;
    }

    const self = this;

    // chart js OPTIONS object. See https://www.chartjs.org/docs/latest/getting-started/usage.html
    return {
      
      tooltips: {
        // callbacks: {
        //         label: (tooltipItem: any, data: any) => {
        //             return tooltipItem.yLabel.toFixed(2).replace(/\d(?=(\d{3})+\.)/g, '$&,');
        //         },
        //     },
      },
      animation: {
        duration: 0,
      },
      display: self.hasReadData,
      responsive: true,
      scales: {
        x: {
          min: self.xMinShadowed,
          max: self.xMax,
          barThickness: 10,  // number (pixels) or 'flex'
          maxBarThickness: 10, // number (pixels)
          offset: true,
          type: 'time',
          time: {
              unit: self.timeUnit,
              displayFormats: {
                  hour: self.displayFormat
              }
          },
          // add this: 
          display: true,
          title: {
            display: true,
            text: 'Date',
            color: self.textColour,
            font: {
              size: 16,
              weight: 'bold',
            }
          },
          // grid: {
          //   color: '#1E3752',
          //   display: true,
          // },
          ticks: {
            // major: {
              fontStyle: 'bold',
              color: self.textColour,
            // },
          },
        },
        y: {
          min: self.yMin,
          max: (self.yMax > 0 ? self.yMax : undefined),
          display: true,
          title: {
            display: true,
            text: self.ylabel,
            color: self.textColour,
            font: {
              size: 16,
              weight: 'bold',
            }
          },
          // grid: {
          //     color: '#1E3752',
          //     display: true,
          // },
          ticks: {
            color: self.textColour, // To format the ticks, coming on the axis/lables which we are passing.
          },
        },
      },
      plugins: {
        title: {
          display: true,
          text: self.title,
          color: self.textColour,
        },
        legend: {
          display: true,
          onHover: self.onLegendHover,
          onClick: self.onLegendClick,
          labels: {
              color: self.textColour,
              // fontSize: 18,
          },
        },
        horizontal: {
          canvasColour: self.canvasColour,
          lines: self.horizontal_lines,
        },
      },
    };
  }

  public get myChartData(): object {
    // chart js DATA object. See https://www.chartjs.org/docs/latest/getting-started/usage.html
    const self = this;

    const datasets: object[] = [];

    if (!self.hasReadData) {
      return {};
    }

    // if (self.rollingAverage) {
    //   datasets.push({
    //     // barPercentage: 0.7,
    //     label: 'Previous 31 days Avg',
    //     type: 'line',
    //     data: self.data_rolling_average,
    //     fill: false,
    //     backgroundColor: 'rgba(240, 155, 21, 1.0)',
    //     borderColor: 'rgba(240, 155, 21, 1.0)',
    //     // borderWidth: 2,
    //   });
    // }

    // datasets.push({
    //   label: self.chartRefs[0]['title'],
    //   data: self.data,
    //   fill: false,
    //   backgroundColor: self.chartRefs[0]['backgroundColor'],
    //   borderColor: self.chartRefs[0]['borderColor'],
    //   // borderWidth: 1,
    // });


    // console.log("self.chartAttributes: ", self.chartAttributes);

    if(self.chartAttributes === undefined) {
      console.error("chartAttributes must match shown plots in quantity");
    }

    // console.log("self.data.length: ", self.data.length);

    for (let i = 0; i < self.data.length ; i++) {
      
      const attribute = self.chartAttributes[i];

      if(attribute === undefined) {
        continue;
      }

      // console.log("self.chartAttributes[i]", self.chartAttributes[i]);

      const index = attribute['data_column_index'];

      let ds = self.data[index];

      if(attribute['visible'] == false) {
        continue;
      }

      datasets.push({
        label: attribute['title'],
        data: ds,
        type: attribute['type'],
        fill: false,
        backgroundColor: attribute['backgroundColor'],
        borderColor: attribute['borderColor'],
      });
    }


  //  console.log(datasets);

    return {
      datasets: datasets,
    };
  }

  protected onLegendHover(event: any, legendItem: any, legend: any) {

  }

  protected async onLegendClick(event: any, legendItem: any, legend: any) {

    const self = this;
    const userinfo = await this.$store.cache.dispatch('requestUserInfo');

    if (userinfo === null) {
      return;
    }

    const isAdmin = (userinfo.is_admin === 'T');

    if (isAdmin && self.chartAttributes.length > 0) {

      let index = legendItem['datasetIndex'];

      // rolling avg not an actual ref so don't include it
      if (self.rollingAverage) {

        if (index === 0) {
          return;
        }

        index -= 1;
      }

    //  console.info(index, self.chartAttributes);

     // const ref: IPortfolioChartRef = self.chartRefs[index];
      // alert(JSON.stringify(ref));

     // const start_dt = DateTime.now().minus({'days':30}).toFormat('MM-dd-yyyy');
     // const end_dt = DateTime.now().toFormat('MM-dd-yyyy');

     // self.$router.push({name: 'bcp', query: {ids: base64url.encode(JSON.stringify([ref])), aggregate: 'avg', resolution: 'day', start_day: start_dt, end_day: end_dt}});

      // self.$router.push({name: 'bcp', params: {ids: JSON.stringify([ref]), aggregate: 'avg', resolution: 'day', start_day: start_dt, end_day: end_dt}});


      // <router-link :to="{name: 'bcp', query: {ids: props.item.energy_refs, aggregate: 'avg', resolution: 'day', start_day: start_day_str, end_day: end_day_str}}">
    }
  }

  // protected get localxMin() {
  //   return this.xMinShadowed;
  // }

  // protected set localxMin(value) {
  //   this.xMinShadowed = value;
  // }

}
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped lang="css">
[v-cloak] {
  display: none;
}

/* .reading-chart {
  display: flex;
  width: 100%;
  height: 100%;
  justify-content: space-around;
} */
</style>